/**
 * Sysadmin MCP catalog — create new server (Wave 4 PR-F-rebuild).
 *
 * Single-page form (NOT a wizard — MCP catalog has no nested engines
 * or chains the way Argus Models does). Authors a draft row that lands
 * with isPublished=false; sysadmin then iterates on auth scheme +
 * manifest from the detail page before publishing.
 *
 * On 201 navigates to /sysadmin/catalog/mcp/$id.
 * On 409 SLUG_TAKEN surfaces an inline field-level error.
 */

import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Network,
  ArrowLeft,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateMcpCatalog,
  MCP_HOSTING_MODES,
  MCP_PROTOCOLS,
  MCP_TRUST_LEVELS,
  MCP_MARKETPLACE_CATEGORIES,
  type CreateCatalogInput,
} from '@/lib/use-sysadmin-mcp';

export const Route = createFileRoute('/sysadmin/catalog/mcp/new')({
  component: NewMcpServerPage,
});

// Form schema does not use .default() because react-hook-form + Zod's
// resolver disagree on whether a defaulted field is "required" in the
// inferred type vs the input. We supply defaults via useForm's
// `defaultValues` and keep all fields explicit in the schema.
const FormSchema = z.object({
  slug: z
    .string()
    .min(2, 'At least 2 characters')
    .max(80, 'At most 80 characters')
    .regex(/^[a-z][a-z0-9_-]*$/, 'Lowercase letters, digits, underscore, dash; starts with a letter'),
  displayName: z.string().min(1, 'Display name required').max(200),
  description: z.string().min(1, 'Description required'),
  icon: z.string().min(1).max(50),
  hostingMode: z.enum(MCP_HOSTING_MODES),
  protocol: z.enum(MCP_PROTOCOLS),
  marketplaceCategory: z.enum(MCP_MARKETPLACE_CATEGORIES),
  trustLevel: z.enum(MCP_TRUST_LEVELS),
  author: z.string().min(1).max(120),
  license: z.string().min(1).max(40),
  dockerImage: z.string().optional(),
  installInstructions: z.string().optional(),
  requiresAuth: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

function NewMcpServerPage() {
  const navigate = useNavigate();
  const createMutation = useCreateMcpCatalog();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    // `as any` here matches the established convention in
    // models.new.tsx — Zod 4 + @hookform/resolvers/zod has a known
    // type-mismatch over $ZodType vs ZodObject internals.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(FormSchema as any),
    defaultValues: {
      slug: '',
      displayName: '',
      description: '',
      icon: 'plug',
      hostingMode: 'remote_hosted',
      protocol: 'http',
      marketplaceCategory: 'productivity',
      trustLevel: 'verified',
      author: 'ArgusAI',
      license: 'Apache-2.0',
      dockerImage: '',
      installInstructions: '',
      requiresAuth: false,
    },
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    const input: CreateCatalogInput = {
      slug: values.slug,
      displayName: values.displayName,
      description: values.description,
      icon: values.icon,
      hostingMode: values.hostingMode,
      protocol: values.protocol,
      marketplaceCategory: values.marketplaceCategory,
      trustLevel: values.trustLevel,
      author: values.author,
      license: values.license,
      dockerImage: values.dockerImage ?? null,
      installInstructions: values.installInstructions || undefined,
      requiresAuth: values.requiresAuth,
    };
    try {
      const result = await createMutation.mutateAsync(input);
      navigate({
        to: '/sysadmin/catalog/mcp/$id',
        params: { id: result.id },
      });
    } catch (err) {
      // Try to surface the API's structured error message.
      const message =
        err instanceof Error ? err.message : 'Failed to create MCP server.';
      setServerError(message);
      // Special-case slug collision so the slug field flags it.
      if (message.includes('SLUG_TAKEN') || message.toLowerCase().includes('slug')) {
        form.setError('slug', {
          type: 'manual',
          message: 'A server with this slug already exists.',
        });
      }
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <Link
          to="/sysadmin/catalog/mcp"
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to catalog
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 mt-2">
          <Network className="h-6 w-6" />
          New MCP server
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Authors a draft row in the catalog. After save you can configure the
          auth scheme + manifest before publishing.
        </p>
      </div>

      {serverError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{serverError}</span>
        </div>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Identity section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Identity</CardTitle>
            <CardDescription>
              Stable slug + display name. The slug is immutable after create.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                placeholder="e.g. github_mcp"
                {...form.register('slug')}
              />
              {form.formState.errors.slug && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.slug.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Lowercase, kebab/snake case; immutable after create.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="displayName">Display name</Label>
              <Input id="displayName" {...form.register('displayName')} />
              {form.formState.errors.displayName && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.displayName.message}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" rows={3} {...form.register('description')} />
              {form.formState.errors.description && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="icon">Icon (lucide name)</Label>
              <Input id="icon" placeholder="plug" {...form.register('icon')} />
            </div>
          </CardContent>
        </Card>

        {/* Hosting + protocol */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hosting</CardTitle>
            <CardDescription>
              How the MCP server runs in production.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Hosting mode</Label>
              <Select
                value={form.watch('hostingMode')}
                onValueChange={(v) => form.setValue('hostingMode', v as never)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MCP_HOSTING_MODES.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m === 'platform_hosted'
                        ? 'Platform-hosted'
                        : m === 'remote_hosted'
                          ? 'Remote-hosted'
                          : 'REST-wrapped'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Protocol</Label>
              <Select
                value={form.watch('protocol')}
                onValueChange={(v) => form.setValue('protocol', v as never)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MCP_PROTOCOLS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2 md:col-span-2">
              <Label htmlFor="dockerImage">Docker image (optional)</Label>
              <Input
                id="dockerImage"
                placeholder="e.g. argusai/mcp-github:1.0.0"
                {...form.register('dockerImage')}
              />
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                id="requiresAuth"
                {...form.register('requiresAuth')}
                className="rounded border-input"
              />
              <Label htmlFor="requiresAuth" className="cursor-pointer">
                Requires authentication
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Marketplace metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Marketplace metadata</CardTitle>
            <CardDescription>
              How tenants discover this server. Trust level and category drive
              filtering on the tenant-facing marketplace page.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Category</Label>
              <Select
                value={form.watch('marketplaceCategory')}
                onValueChange={(v) =>
                  form.setValue('marketplaceCategory', v as never)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MCP_MARKETPLACE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Trust level</Label>
              <Select
                value={form.watch('trustLevel')}
                onValueChange={(v) => form.setValue('trustLevel', v as never)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MCP_TRUST_LEVELS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="author">Author</Label>
              <Input id="author" {...form.register('author')} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="license">License</Label>
              <Input id="license" {...form.register('license')} />
            </div>
            <div className="grid gap-2 md:col-span-2">
              <Label htmlFor="installInstructions">Install instructions (optional)</Label>
              <Textarea
                id="installInstructions"
                rows={3}
                placeholder="Markdown-friendly notes shown on the install page."
                {...form.register('installInstructions')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/sysadmin/catalog/mcp" className={buttonVariants({ variant: 'outline' })}>
            Cancel
          </Link>
          <Button
            type="submit"
            disabled={createMutation.isPending}
            className="min-w-32"
          >
            {createMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Create draft
          </Button>
        </div>
      </form>
    </div>
  );
}
